import { Optional } from '@angular/core';
import { SimulationObject } from 'src/app/core/interfaces/simulation-object';
import { Simulation } from 'src/app/core/models/simulation.model';
import { ObjectState } from 'src/app/shared/utils/object-state';
import { DeleteRequestAnswer } from 'src/app/shared/utils/simulation-manager-aux/delete-request-answer';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { SimulationManagerService } from '../../simulation-manager.service';
import { AccessoriesService } from '../accessories/accessories.service';
import { LineService } from '../line/line.service';
import { MapsService } from '../maps/maps.service';
import { MaterialsService } from '../materials/materials.service';
import { TitleCopyService } from '../title-copy/title-copy.service';

export abstract class AbstractObjectService {
  protected _objectFieldName: string;
  protected _accessory: boolean;
  protected _material: boolean;
  protected _bindings: boolean;

  constructor(
    protected simulationService: SimulationManagerService,
    @Optional() protected accessoriesService: AccessoriesService,
    @Optional() protected materialsService: MaterialsService,
    protected dataSharingService: DataSharingService,
    protected mapsService: MapsService,
    @Optional() protected titleCopyService: TitleCopyService,
    @Optional() protected lineService: LineService,
  ) {}

  get objectTypeService(): MaterialsService | AccessoriesService | AbstractObjectService {
    if (this._accessory) {
      return this.accessoriesService;
    }
    if (this._material) {
      return this.materialsService;
    }
    return this;
  }

  get simulation(): Simulation {
    return this.simulationService.simulation;
  }

  abstract create(): void;
  abstract duplicate(originalId: number): SimulationObject;
  abstract save(id: number, payload: any): void;

  hasAny(): boolean {
    return !!this.simulation?.[this._objectFieldName]?.length;
  }

  getById(id: number): SimulationObject | any {
    const answer = this.simulation?.[this._objectFieldName]?.find((obj) => obj.internalId === id);
    return answer || null;
  }

  getNew(): SimulationObject | any {
    this.create();
    const simulationField = this.simulation[this._objectFieldName];
    return simulationField[simulationField.length - 1];
  }

  delete(id: number): DeleteRequestAnswer {
    const objectIsBeingUsed = this.isObjectInUse(id);

    if (objectIsBeingUsed !== null) {
      return new DeleteRequestAnswer(ObjectState.objectIsBeingUsed, objectIsBeingUsed);
    }

    this.simulation[this._objectFieldName] = this.simulation[this._objectFieldName].filter(
      (obj) => obj.internalId !== id,
    );
    this.mapsService.deleteFromMaps(id);
    this.simulationService.changeSimulation();
    this.dataSharingService.localChanges$.next();
    this.dataSharingService.updateCatenary$.next();

    return new DeleteRequestAnswer(ObjectState.objectWasDeleted, null);
  }

  updateRelatedLines(id: number): void {
    const usedByLinesWithIds = this.isObjectByIdInUse(id);
    this.lineService.markToUpdate(usedByLinesWithIds);
  }

  isObjectInUse(id: number): string[] | null {
    if (this._accessory) {
      return this.accessoriesService.isObjectInUse(id);
    }
    if (this._material) {
      return this.materialsService.isObjectInUse(id);
    }
  }

  isObjectByIdInUse(id: number): number[] | null {
    if (this._accessory) {
      return this.accessoriesService.isObjectByIdInUse(id);
    }
    if (this._material) {
      return this.materialsService.isObjectByIdInUse(id);
    }
  }
}
